import React, { useCallback, useState, useMemo } from "react";
import styled from "styled-components";
import { AllTokens, TokenForFE } from "../../types/StateTypes";
import { PhoenixStyledTooltip } from "../Dumb/PhoenixStyledTooltip";
import { theme } from "./../../utils/theme";
import { AppText, SkeletonBlock } from "./../UI";
import { PhoenixAppButton, PhoenixInput } from "./../UI/Phoenix";

// Helper function to normalize search text by removing brackets and underscores
const normalizeSearchText = (text: string): string => {
  return text.toLowerCase().replace(/[\[\]_]/g, "");
};

const Tokens = ({
  loading,
  tokens,
  ignoreOverflow,
  searchInput,
  height,
  showTokenSectionTitle = true,
}: {
  loading: boolean;
  tokens: AllTokens | undefined;
  ignoreOverflow?: boolean;
  searchInput?: boolean;
  height?: number;
  showTokenSectionTitle?: boolean;
}) => {
  const [searchText, setSearchText] = useState("");

  // Normalize the search text once
  const normalizedSearchText = useMemo(() => normalizeSearchText(searchText), [searchText]);

  const handleTooltipAfterShow = useCallback((e) => {
    e.stopPropagation();
    // get the token value
    const clickedButton = e?.currentTarget;

    const token = clickedButton?.textContent;

    if (token) navigator?.clipboard?.writeText(token);
  }, []);

  // Memoize the filtered tokens to avoid recalculating on every render
  const filteredTokenSections = useMemo(() => {
    if (!tokens || loading) return [];

    return Object.entries(tokens)
      .map(([sectionLabel, sectionTokens]) => {
        // Filter tokens based on normalized search text
        const filteredTokens = sectionTokens.filter((token) => {
          if (!normalizedSearchText) return true;
          const normalizedToken = normalizeSearchText(token.token_formatted);
          return normalizedToken.includes(normalizedSearchText);
        });

        return {
          sectionLabel,
          sectionTokens: filteredTokens,
          hasVisibleTokens: filteredTokens.length > 0,
        };
      })
      .filter((section) => section.hasVisibleTokens);
  }, [tokens, normalizedSearchText, loading]);

  if (loading) {
    const loadingPattern = [
      [100, 1],
      [90, 1.5],
      [120, 2],
      [72, 2.5],
      [90, 3],
      [120, 1.5],
      [72, 2],
      [100, 2],
      [90, 2.5],
      [80, 3],
      [72, 1.5],
      [100, 2],
      [120, 2.5],
      [80, 3],
      [100, 3.5],
    ];
    return (
      <Main height={height}>
        <Header>
          <AppText fontSize={12} fontWeight={500} color={theme.BLACK_COLOR}>
            Tokens
          </AppText>
        </Header>
        <ScrollContent>
          <TokensContainer ignoreOverflow={ignoreOverflow}>
            {loadingPattern?.map((p, index) => (
              <SkeletonBlock key={index} width={p[0]} height={38} borderRadius="8px" delayNumber={p[1]} />
            ))}
          </TokensContainer>

          <AppText fontSize={12} fontWeight={500} color={theme.BLACK_COLOR} style={{ marginTop: "16px" }}>
            Other Tokens
          </AppText>
          <TokensContainer ignoreOverflow={ignoreOverflow}>
            {loadingPattern?.map((p, index) => (
              <SkeletonBlock key={index} width={p[0]} height={38} borderRadius="8px" delayNumber={p[1] + 1.5} />
            ))}
          </TokensContainer>
        </ScrollContent>
      </Main>
    );
  }

  const TokensSection = ({
    sectionTokens,
    sectionLabel,
    ignoreOverflow,
  }: {
    sectionTokens: TokenForFE[];
    sectionLabel: string;
    ignoreOverflow?: boolean;
  }) => {
    return (
      <>
        <PhoenixStyledTooltip
          id="tokens-tooltip"
          event="click"
          eventOff="mouseleave"
          delayHide={800}
          backgroundColor={theme.SUCCESS200}
          textColor={theme.SUCCESS500}
          place="bottom"
          afterShow={handleTooltipAfterShow}
        />
        <PhoenixStyledTooltip id="fallback-tooltip" width={250} />
        {showTokenSectionTitle && (
          <AppText fontSize={12} fontWeight={500} style={{ color: theme.BLACK_COLOR }}>
            {sectionLabel} Tokens
          </AppText>
        )}
        <TokensContainer ignoreOverflow={ignoreOverflow}>
          {sectionTokens.map((token: TokenForFE) => (
            <FallbackHoverDiv
              key={token?.token}
              id="fallback-tooltip"
              data-tip={
                sectionLabel === "Custom Field"
                  ? "If a custom field token is used and has more than one value, all values will be added and comma separated"
                  : token?.fallback?.length
                  ? `If there is no value in this field, we will use "${token?.fallback}"`
                  : `If there is no value in this field, the token will be blank`
              }
              data-for="fallback-tooltip"
            >
              <PhoenixAppButton
                data-tip="Token copied to clipboard"
                data-for="tokens-tooltip"
                buttonType={"tertiary"}
                variant="brand"
                buttonTextFontSize={8}
                padding={"4px 8px"}
              >
                {token?.token_formatted}
              </PhoenixAppButton>
            </FallbackHoverDiv>
          ))}
        </TokensContainer>
      </>
    );
  };

  return (
    <Main height={height}>
      <Header>
        <AppText fontSize={12} fontWeight={500} color={theme.BLACK_COLOR}>
          Tokens
        </AppText>
        <AppText fontSize={12} fontWeight={400}>
          Select a token below and paste it into your message to automatically include the corresponding information.
        </AppText>
        {searchInput && <SearchInput searchText={searchText} setSearchText={setSearchText} />}
      </Header>

      <ScrollContent>
        {filteredTokenSections.map(({ sectionLabel, sectionTokens }) => (
          <TokensSection
            key={sectionLabel}
            sectionTokens={sectionTokens}
            sectionLabel={sectionLabel}
            ignoreOverflow={ignoreOverflow}
          />
        ))}
      </ScrollContent>
    </Main>
  );
};

const SearchInput = ({ searchText, setSearchText }: { searchText: string; setSearchText: (text: string) => void }) => {
  return (
    <div
      style={{
        padding: 4,
      }}
    >
      <PhoenixInput
        value={searchText}
        onChange={(e: any) => setSearchText(e.target.value)}
        placeholder="Search tokens..."
        searchInput
        displayNoContextText
        width={376}
      />
    </div>
  );
};

const FallbackHoverDiv = styled.div`
  &:hover {
    cursor: pointer;
  }
`;

const TokensContainer = styled.div<{ ignoreOverflow?: boolean }>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;
`;

const ScrollContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  flex: 1;
`;

const Main = styled.div<{ height?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: ${({ height }) => (height ? `${height}px` : "auto")};
  overflow: hidden;
  min-height: 300px;
`;

export { Tokens };
